import {
  ChevronDownIcon,
  CopyIcon,
  MessageCircleIcon,
  PhoneIcon,
} from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { misc } from "@/lib/misc";

export const PhoneDropdown = ({
  isReadOnly = false,
  phone,
  onCallInQuo,
  onMessageCandidate,
}: {
  // Viewing surfaces (the application review portal) show the number as
  // plain text with no actions behind it.
  isReadOnly?: boolean;
  phone?: string;
  onCallInQuo?: () => void;
  onMessageCandidate?: () => void;
}) => {
  /* Helpers */
  const formattedPhone = phone
    ? misc.formatPhoneNumberOnCopy(phone.replace(/\D+/g, ""))
    : "";

  /* Handlers */
  const handleCopyPhone = () => {
    if (!phone) return;

    void navigator.clipboard.writeText(misc.formatPhoneNumberOnCopy(phone));
  };

  /* Render */
  const render = () => {
    if (!phone) return;
    if (isReadOnly) {
      return (
        <Row className="items-center gap-2">
          <PhoneIcon
            className="h-4 w-4 shrink-0 text-slate-400"
            strokeWidth={1.5}
          />
          <Text className="text-xs font-normal text-slate-500">
            {formattedPhone}
          </Text>
        </Row>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="group/phone -m-2 h-auto gap-2 rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-950"
            type="button"
            variant="ghost"
          >
            <PhoneIcon
              className="h-4 w-4 shrink-0 text-slate-400 group-hover/phone:hidden group-data-[state=open]/phone:hidden"
              strokeWidth={1.5}
            />
            <ChevronDownIcon
              className="hidden h-4 w-4 shrink-0 text-slate-400 group-hover/phone:block group-hover/phone:text-slate-950 group-data-[state=open]/phone:block group-data-[state=open]/phone:text-slate-950"
              strokeWidth={1.5}
            />
            <Text className="text-xs font-normal text-slate-500 group-hover/phone:text-slate-950 group-data-[state=open]/phone:text-slate-950">
              {formattedPhone}
            </Text>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {onCallInQuo ? (
            <DropdownMenuItem onClick={onCallInQuo}>
              <PhoneIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
              Call in Quo
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={onMessageCandidate}>
            <MessageCircleIcon
              className="h-4 w-4 text-slate-400"
              strokeWidth={1.5}
            />
            Message candidate
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyPhone}>
            <CopyIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            Copy phone number
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return render();
};
